import { useMountEffect } from "@react-hookz/web";
import { ArrowLeftIcon, ArrowRightIcon } from "@smartsheet/lodestar-icons";
import { ChevronLeftIcon, ChevronRightIcon } from "@smartsheet/ursa-icons";
import add from "date-fns/add";
import isSameMonth from "date-fns/isSameMonth";
import isToday from "date-fns/isToday";
import setYear from "date-fns/setYear";
import sub from "date-fns/sub";
import React, { forwardRef, KeyboardEventHandler, ReactNode, Ref, useMemo, useRef } from "react";
import ReactFocusLock from "react-focus-lock";
import { SupportedLocales } from "../../../../i18n/constants";
import { useI18n } from "../../../../i18n/i18n";
import { useCombinedThemeMode } from "../../../../util/theme/useCombinedThemeMode";
import { DatePickerDate } from "../../types";
import { getMonthMatrix, isDayDisabled } from "../../util/calendar";
import { DatePickerDayBox } from "../styled/DatePickerDayBox";
import { DatePickerDayContainer } from "../styled/DatePickerDayContainer";
import { DatePickerDayOfWeek } from "../styled/DatePickerDayOfWeek";
import { DatePickerDialogMonthContainer } from "../styled/DatePickerDialogMonthContainer";
import { DatePickerDialogStyled } from "../styled/DatePickerDialogStyled";
import { DatePickerFooter } from "../styled/DatePickerFooter";
import { DatePickerFooterButton } from "../styled/DatePickerFooterButton";
import { DatePickerMonthName } from "../styled/DatePickerMonthName";
import { DatePickerMonthSwitchButton } from "../styled/DatePickerMonthSwitchButton";
import { datePickerTheme, getTypeRampPropsDayOfWeek, getTypeRampPropsMonthYear } from "../styled/datePickerTheme";
import { DayTracker } from "../util/DayTracker";
import { canMoveToNextMonth, canMoveToPreviousMonth } from "../util/datePickerFrameUtil";
import { DatePickerYearSelector } from "./DatePickerYearSelector";

interface ChildRenderProps {
    day: Date;
    htmlContent: string;
    isDisabled: boolean;
    isToday: boolean;
    key: string;
    onKeyDown: KeyboardEventHandler<HTMLButtonElement>;
    ref: Ref<HTMLButtonElement>;
}

export interface DatePickerFrameProps {
    /**
     * The currently active date in the picker. This is used in key handlers.
     */
    activeDate: Date;

    /**
     * The currently visible month in the picker.
     */
    activeMonth: Date;

    /**
     * A Function called to get a single Date rendered.
     * This is called for each displayed date in the Calender, each render.
     */
    children: (props: ChildRenderProps) => ReactNode;

    /**
     * The locale to render strings and dates in.
     */
    locale: SupportedLocales;

    /**
     * If provided, will render a button in the dialog's footer that will clear the selected date
     * and call requestDateChange with null. Both the single date and date range pickers will use
     * this but we're allowing undefined since this prop comes from the user and we want them to see
     * a warning message if they don't provide it. If we made it required, the DatePickerDialog and
     * DateRangePickerDialog would have to set a default value for it which would skip the nullish
     * check.
     */
    onClearClicked: (() => void) | undefined;

    /**
     * Called when the user clicks the cancel button, if applicable, or
     * presses escape.
     */
    onCloseRequested: (refocusTarget: boolean) => void;

    /**
     * Handler this component will call when it would like to request
     * that a new date be activated.
     */
    requestDateChange: (newDate: Date) => void;

    /**
     * If true, the "Apply" button will be disabled.
     */
    applyIsDisabled?: boolean;

    /**
     * The label to be applied to this component's root.
     */
    "aria-label"?: string;

    /**
     * The id of this component's labelling HTML element.
     */
    "aria-labelledby"?: string;

    /**
     * A className to apply to the root element of this component.
     */
    className?: string;

    /**
     * A `clientId` prop is provided for specified elements, which is a unique string that
     * appears as a data attribute `data-client-id` in the rendered code, serving as a hook
     * for automated tests
     */
    clientId?: string;

    /**
     * If provided, will be passed along to the element that contains the calendar grid.
     * Used for focus management in the range scenario.
     */
    dayContainerRef?: Ref<HTMLDivElement>;

    /**
     * If provided, called for each day in the current calendar viewport to check
     * if it should be selectable or not.
     *
     * Works alongside minDate/maxDate as well.
     */
    dayIsDisabled?: (date: DatePickerDate) => boolean;

    /**
     * The HTML id of the container.
     */
    id?: string;

    /**
     * The maximum allowed date to select.
     *
     * Works together with dayIsDisabled as well.
     */
    maxDate?: DatePickerDate;

    /**
     * The minimum allowed date to select.
     *
     * Works together with dayIsDisabled as well.
     */
    minDate?: DatePickerDate;

    /**
     * Called when the "Apply" button is clicked.
     */
    onApplyClicked?: () => void;

    /**
     * Handler this component will call when it would like to request
     * that the selected Date be committed back to the user.
     */
    onSpaceOrEnter?: () => void;

    /**
     * If provided, will render a button in the dialog's footer that will select today's date when
     * clicked. In a range picker, TODO. This is kinda a leaky abstraction since it's only used in
     * the single date picker.
     */
    onTodayClicked?: () => void;
}

export const DatePickerFrame = forwardRef<HTMLDivElement, DatePickerFrameProps>(
    (
        {
            activeDate,
            activeMonth,
            applyIsDisabled,
            "aria-label": ariaLabel,
            "aria-labelledby": ariaLabelledby,
            children,
            className,
            clientId,
            dayContainerRef,
            dayIsDisabled,
            id,
            locale,
            maxDate,
            minDate,
            onApplyClicked,
            onClearClicked,
            onCloseRequested,
            onSpaceOrEnter,
            onTodayClicked,
            requestDateChange,
        },
        ref
    ) => {
        const { i18n } = useI18n();
        const { current: dayTracker } = useRef(new DayTracker());
        const themeMode = useCombinedThemeMode();

        // getMonthMatrix is not a slow operation, but memoizing it means the dates it returns are
        // stable across renders which helps React out a lot.
        const monthMatrix = useMemo(() => getMonthMatrix(activeMonth), [activeMonth]);

        const dayIndices = [0, 1, 2, 3, 4, 5, 6] as const;

        const daySetterRef = (dayBtn: HTMLButtonElement | null, date: Date) => {
            if (!dayBtn) {
                dayTracker.setDate(date, null);
            } else {
                dayTracker.setDate(date, dayBtn);
            }
        };

        const getDayLabel = (dayIndex: (typeof dayIndices)[number]) => {
            const dayDate = new Date(2023, 0, 1 + (dayIndex % 7)); // Jan 01, 2023 was a Sunday
            const getFormattedLabel = (type: "short" | "narrow") => {
                // Get the applicable localized string and strip any periods from the end of it
                return dayDate.toLocaleDateString(locale, { weekday: type }).replace(/\./g, "");
            };
            // TODO Move this into datePickerTheme
            switch (themeMode) {
                case "ursa-light":
                    return getFormattedLabel("short");
                case "lodestar-light":
                default:
                    return getFormattedLabel("narrow");
            }
        };

        const handleEscapeOnKeyDown: KeyboardEventHandler<HTMLElement> = (event) => {
            switch (event.key) {
                case "Escape":
                    onCloseRequested(true);
                    break;
            }
        };

        const prevMonthEnabled = canMoveToPreviousMonth(activeDate, minDate);
        const nextMonthEnabled = canMoveToNextMonth(activeDate, maxDate);

        const handleDayKeyDown: KeyboardEventHandler<HTMLButtonElement> = (event) => {
            let newDate: Date | null = null;
            switch (event.key) {
                case "Enter":
                case " ":
                    if (!isDayDisabled(activeDate, dayIsDisabled, minDate, maxDate)) {
                        // Don't let this propagate to a click later on, we
                        // don't want to double-handle this event.
                        event.preventDefault();
                        onSpaceOrEnter?.();
                    }
                    return;
                case "ArrowUp":
                    newDate = sub(activeDate, { weeks: 1 });
                    break;
                case "ArrowDown":
                    newDate = add(activeDate, { weeks: 1 });
                    break;
                case "ArrowLeft":
                    newDate = sub(activeDate, { days: 1 });
                    break;
                case "ArrowRight":
                    newDate = add(activeDate, { days: 1 });
                    break;
                case "Home":
                    newDate = sub(activeDate, { years: 1 });
                    break;
                case "End":
                    newDate = add(activeDate, { years: 1 });
                    break;
                case "PageDown":
                    newDate = add(activeDate, { months: 1 });
                    break;
                case "PageUp":
                    newDate = sub(activeDate, { months: 1 });
                    break;
            }

            if (newDate) {
                // If yearRange is provided, return early if the new date is out of that range.
                if (minDate && newDate.getFullYear() < minDate.year) {
                    // TODO we should notify via a live region that the requested move is out of bounds.
                    return;
                } else if (maxDate && newDate.getFullYear() > maxDate.year) {
                    // TODO we should notify via a live region that the requested move is out of bounds.
                    return;
                }

                event.preventDefault();
                event.stopPropagation();

                requestDateChange(newDate);
                const finalNewDate = newDate;
                requestAnimationFrame(() => {
                    const newBtn = dayTracker.getDate(finalNewDate);
                    if (newBtn) {
                        newBtn.focus();
                    }
                });
            }
        };

        // Set up the initial focused element on mount.
        useMountEffect(() => {
            const initialDateBtn = dayTracker.getDate(activeDate);
            if (initialDateBtn) {
                initialDateBtn.focus();
            }
        });

        return (
            <ReactFocusLock
                // TODO: add tests to ensure this is functioning correctly, need to ignore TS error because of ReactFocusLock API
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                as={DatePickerDialogStyled}
                ref={ref}
                className={className}
                // Naw, we know what we're doing :)
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={false}
                lockProps={{
                    onKeyDown: handleEscapeOnKeyDown,
                    "aria-label": ariaLabel,
                    "aria-labelledby": ariaLabelledby,
                    "data-client-id": clientId,
                    // Using role="application" here because otherwise some screen readers will
                    // override the arrow buttons to make up down navigation through buttons in order.
                    // further, JAWS REALLY has trouble with the focus moving in and out of this dialog,
                    // so we are just going to manage everything inside here.
                    role: "application",
                    id,
                    themeMode,
                }}
            >
                <DatePickerDialogMonthContainer>
                    <DatePickerMonthSwitchButton
                        themeMode={themeMode}
                        size="medium"
                        appearance={datePickerTheme.topButtonType(themeMode)}
                        aria-label={i18n._(
                            /*i18n*/ {
                                id: "Show previous month",
                                comment: "The aria label for the button that goes back one month.",
                            }
                        )}
                        onClick={() => {
                            requestDateChange(sub(activeDate, { months: 1 }));
                        }}
                        iconBefore={arrowIconsForTheme[themeMode].left}
                        isDisabled={!prevMonthEnabled}
                    />
                    <DatePickerMonthSwitchButton
                        themeMode={themeMode}
                        size="medium"
                        appearance={datePickerTheme.topButtonType(themeMode)}
                        aria-label={i18n._(
                            /*i18n*/ {
                                id: "Show next month",
                                comment: "The aria label for the button that goes forward one month.",
                            }
                        )}
                        onClick={() => {
                            requestDateChange(add(activeDate, { months: 1 }));
                        }}
                        iconBefore={arrowIconsForTheme[themeMode].right}
                        isDisabled={!nextMonthEnabled}
                    />
                    <DatePickerMonthName
                        {...getTypeRampPropsMonthYear(themeMode)}
                        aria-live="polite"
                        renderAs="div"
                        omitTooltip
                    >
                        {activeMonth.toLocaleDateString(locale, { month: "long" })}
                    </DatePickerMonthName>
                    <DatePickerYearSelector
                        maxDate={maxDate}
                        minDate={minDate}
                        selectedYear={activeMonth.getFullYear()}
                        locale={locale}
                        onChange={(newYear) => {
                            requestDateChange(setYear(activeDate, newYear));
                        }}
                    />
                </DatePickerDialogMonthContainer>

                <DatePickerDayContainer ref={dayContainerRef} themeMode={themeMode}>
                    {dayIndices.map((i) => (
                        <DatePickerDayOfWeek {...getTypeRampPropsDayOfWeek(themeMode)} key={i} themeMode={themeMode}>
                            {getDayLabel(i)}
                        </DatePickerDayOfWeek>
                    ))}
                    {monthMatrix.map((day) => {
                        if (!isSameMonth(day, activeMonth)) {
                            // Omit the days from other months from the grid.
                            // Add a spacer element to align everything properly.
                            return (
                                <DatePickerDayBox key={`${day.getMonth()}-${day.getDate()}`} themeMode={themeMode}>
                                    <></>
                                </DatePickerDayBox>
                            );
                        } else {
                            return children({
                                day,
                                htmlContent: `${day.getDate()}`,
                                isDisabled: isDayDisabled(day, dayIsDisabled, minDate, maxDate),
                                isToday: isToday(day),
                                key: `${day.getMonth()}-${day.getDate()}`,
                                onKeyDown: handleDayKeyDown,
                                ref: (btn) => daySetterRef(btn, day),
                            });
                        }
                    })}
                </DatePickerDayContainer>

                <DatePickerFooter orientation="row" space="small" themeMode={themeMode}>
                    <DatePickerFooterButton
                        appearance={datePickerTheme.footer.buttonAppearance.standard(themeMode)}
                        onClick={() => {
                            if (process.env.NODE_ENV !== "production") {
                                if (!onClearClicked) {
                                    console.warn(
                                        "You have not provided an onClearClicked handler for the " +
                                            "DatePickerDialog. This means that the clear button will not " +
                                            "do anything when clicked."
                                    );
                                }
                            }
                            onClearClicked?.();
                        }}
                    >
                        {i18n._(
                            /*i18n*/ {
                                id: "Clear",
                                comment: "The text for a button that will clear out the value of a date selection",
                            }
                        )}
                    </DatePickerFooterButton>
                    {onTodayClicked && (
                        <DatePickerFooterButton
                            appearance={datePickerTheme.footer.buttonAppearance.standard(themeMode)}
                            onClick={() => {
                                if (process.env.NODE_ENV !== "production") {
                                    if (!onClearClicked) {
                                        console.warn(
                                            "You have not provided an onClearClicked handler for the " +
                                                "DatePickerDialog. This means that the clear button will not " +
                                                "do anything when clicked."
                                        );
                                    }
                                }
                                onTodayClicked();
                            }}
                        >
                            {i18n._(
                                /*i18n*/ {
                                    id: "Today",
                                    comment:
                                        "The text shown in a button that will select today's date in a date picker.",
                                }
                            )}
                        </DatePickerFooterButton>
                    )}
                    {onApplyClicked && (
                        <DatePickerFooterButton
                            appearance={datePickerTheme.footer.buttonAppearance.apply(themeMode)}
                            isDisabled={applyIsDisabled}
                            onClick={onApplyClicked}
                        >
                            {i18n._(
                                /*i18n*/ {
                                    id: "Apply",
                                    comment: "The text for the button that will apply the currently selected changes.",
                                }
                            )}
                        </DatePickerFooterButton>
                    )}
                </DatePickerFooter>
            </ReactFocusLock>
        );
    }
);

DatePickerFrame.displayName = "DatePickerFrame";

const arrowIconsForTheme = {
    "ursa-light": {
        // These icons have special color requirements that we'll set at the parent Button.
        left: <ChevronLeftIcon size={24} color="inherit" />,
        right: <ChevronRightIcon size={24} color="inherit" />,
    },
    "lodestar-light": {
        left: <ArrowLeftIcon size="medium" />,
        right: <ArrowRightIcon size="medium" />,
    },
    oldestar: {
        left: <ArrowLeftIcon size="medium" />,
        right: <ArrowRightIcon size="medium" />,
    },
};
